import React from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { useAuth } from '../../context/AuthContext';
import { Clock, Wrench, FileText, Bookmark, GitBranch, Inbox, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkspaceCommandCenter = () => {
  const { items, leads, setCurrentView, refreshItems } = useAppState();
  const { token } = useAuth();

  const recentItems = items.slice(-6).reverse();
  
  // Sort tools by click count (descending) and take top 6
  const toolItems = items
    .filter(item => item.category === 'tools')
    .sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0))
    .slice(0, 6);
    
  const pipelineStats = {
    total: leads.length,
    active: leads.filter(l => !['Won', 'Lost'].includes(l.status)).length,
    won: leads.filter(l => l.status === 'Won').length
  };

  const handleToolClick = async (e, toolId, toolUrl) => {
    e.preventDefault(); // Prevent immediate navigation
    try {
      // Track the click with auth token
      await axios.post(
        `${API}/items/${toolId}/track-click`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh items to get updated click counts
      await refreshItems();
      // Open the tool in a new tab
      window.open(toolUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to track click:', error);
      // Still open the tool even if tracking fails
      window.open(toolUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="workspace-container" data-testid="workspace-command-center">
      <div className="workspace-header">
        <h2 className="workspace-title">Command Center</h2>
        <p className="workspace-subtitle">Your agency operations hub</p>
      </div>

      {/* Most Used Tools */}
      {toolItems.length > 0 && (
        <div className="most-used-tools" data-testid="most-used-tools">
          <h3 className="section-title">Most Used Tools</h3>
          <div className="tools-row">
            {toolItems.map(tool => (
              <a
                key={tool.id}
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                className="tool-card"
                data-testid={`tool-card-${tool.id}`}
                onClick={(e) => handleToolClick(e, tool.id, tool.url)}
              >
                <div className="tool-icon-wrapper">
                  <Wrench size={24} className="tool-icon" />
                </div>
                <span className="tool-name">{tool.title}</span>
                <ExternalLink size={14} className="tool-external-icon" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="command-grid">
        {/* Pipeline Overview */}
        <div className="command-card" data-testid="pipeline-overview-card">
          <div className="card-header">
            <GitBranch className="card-icon" size={20} />
            <h3 className="card-title">Pipeline Overview</h3>
          </div>
          <div className="pipeline-stats">
            <div className="stat-item">
              <div className="stat-value">{pipelineStats.total}</div>
              <div className="stat-label">Total Leads</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{pipelineStats.active}</div>
              <div className="stat-label">Active</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{pipelineStats.won}</div>
              <div className="stat-label">Won</div>
            </div>
          </div>
          <button 
            className="view-all-btn"
            data-testid="view-pipeline-btn"
            onClick={() => setCurrentView('pipeline')}
          >
            View Pipeline →
          </button>
        </div>

        {/* Quick Actions */}
        <div className="command-card" data-testid="quick-actions-card">
          <div className="card-header">
            <h3 className="card-title">Quick Actions</h3>
          </div>
          <div className="quick-actions">
            <button 
              className="quick-action-btn"
              data-testid="quick-action-tools"
              onClick={() => setCurrentView('tools')}
            >
              <Wrench size={18} />
              <span>Browse Tools</span>
            </button>
            <button 
              className="quick-action-btn"
              data-testid="quick-action-prompts"
              onClick={() => setCurrentView('prompts')}
            >
              <FileText size={18} />
              <span>View Prompts</span>
            </button>
            <button 
              className="quick-action-btn"
              data-testid="quick-action-capture"
              onClick={() => setCurrentView('capture')}
            >
              <Inbox size={18} />
              <span>Quick Capture</span>
            </button>
          </div>
        </div>

        {/* Recent Items */}
        <div className="command-card command-card-wide" data-testid="recent-items-card">
          <div className="card-header">
            <Clock className="card-icon" size={20} />
            <h3 className="card-title">Recent Items</h3>
          </div>
          <div className="recent-items-list">
            {recentItems.length === 0 ? (
              <p className="empty-state">No items yet. Use Quick Capture to add some!</p>
            ) : (
              recentItems.map(item => {
                const Icon = item.category === 'tools' ? Wrench : 
                           item.category === 'prompts' ? FileText : Bookmark;
                return (
                  <a
                    key={item.id}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="recent-item"
                    data-testid={`recent-item-${item.id}`}
                    onClick={(e) => handleToolClick(e, item.id, item.url)}
                  >
                    <Icon size={16} className="recent-item-icon" />
                    <span className="recent-item-title">{item.title}</span>
                    <span className="recent-item-category">{item.category}</span>
                  </a>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceCommandCenter;


